import { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Image,
  Input,
  VStack,
  Heading,
  Text,
  useToast,
  Link,
} from '@chakra-ui/react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const { signIn } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    
    try {
      setLoading(true);
      await signIn(email, password);
      
      toast({
        title: 'Signed in successfully',
        status: 'success',
        duration: 3000,
      });
      
      navigate('/');
    } catch (error) {
      toast({
        title: 'Sign in failed',
        description: error instanceof Error ? error.message : 'Invalid credentials',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword() {
    if (!email) {
      toast({
        title: 'Enter your email first',
        description: 'Type your email address above, then click "Forgot password?"',
        status: 'info',
        duration: 4000,
      });
      return;
    }

    try {
      setResetLoading(true);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/set-password`,
      });
      if (error) throw error;

      toast({
        title: 'Reset email sent',
        description: 'Check your inbox for a password reset link.',
        status: 'success',
        duration: 6000,
      });
    } catch (error) {
      toast({
        title: 'Failed to send reset email',
        description: error instanceof Error ? error.message : 'Please try again.',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setResetLoading(false);
    }
  }

  return (
    <Box
      minH="100vh"
      bgGradient="linear(to-br, amber.50, white, orange.50)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <Box
        bg="white"
        p={{ base: 8, md: 10 }}
        borderRadius="2xl"
        boxShadow="2xl"
        borderWidth={1}
        borderColor="gray.100"
        maxW="md"
        w="full"
      >
        <VStack spacing={6} align="stretch">
          {/* Header with Icon */}
          <VStack spacing={3}>
            <Image
              src="/images/mesa-logo-for-light-bg.png"
              alt="Mesa Group Consulting"
              maxW="286px"
            />
            <Heading fontSize="xl" textAlign="center" color="gray.900">
              Content Generator
            </Heading>
            <Text textAlign="center" color="gray.600">
              AI-powered blog posts &amp; YouTube scripts
            </Text>
          </VStack>

          <form onSubmit={handleSubmit}>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Email
                </FormLabel>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  size="lg"
                  borderColor="gray.300"
                  _hover={{ borderColor: 'gray.400' }}
                  _focus={{
                    borderColor: 'brand.400',
                    boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                  }}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                  Password
                </FormLabel>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  size="lg"
                  borderColor="gray.300"
                  _hover={{ borderColor: 'gray.400' }}
                  _focus={{
                    borderColor: 'brand.400',
                    boxShadow: '0 0 0 3px rgba(251, 191, 36, 0.1)',
                  }}
                />
              </FormControl>

              <Button
                type="submit"
                width="100%"
                size="lg"
                bg="brand.400"
                color="gray.900"
                fontWeight="semibold"
                _hover={{
                  bg: 'brand.500',
                  transform: 'translateY(-2px)',
                  shadow: 'lg',
                }}
                _active={{
                  transform: 'translateY(0)',
                }}
                isLoading={loading}
                mt={2}
                transition="all 0.2s"
              >
                Sign In
              </Button>

              <Text textAlign="center" mt={1}>
                <Link
                  fontSize="sm"
                  color="brand.600"
                  _hover={{ color: 'brand.700', textDecoration: 'underline' }}
                  onClick={handleForgotPassword}
                  cursor="pointer"
                  opacity={resetLoading ? 0.6 : 1}
                  pointerEvents={resetLoading ? 'none' : 'auto'}
                >
                  {resetLoading ? 'Sending...' : 'Forgot password?'}
                </Link>
              </Text>
            </VStack>
          </form>

          <Text fontSize="xs" color="gray.400" textAlign="center" mt={4}>
            Powered by Mesa Group Consulting
          </Text>
        </VStack>
      </Box>
    </Box>
  );
}
